import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { DASH_COLORS, DASH_FONTS, DASH_TYPE, MIN_TOUCH_PX } from './dashboardTokens';

// Full-screen detail view of one tile. Strictly client-local: opening and
// closing never broadcasts anything (specs/workshop-dashboard.md).

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  /* Above MainViewHeader (z-index 100), whose bar otherwise covered the
     close button; below app-wide dialogs and drawers (1000+). */
  z-index: 150;
  display: flex;
  flex-direction: column;
  background: ${DASH_COLORS.bgGradient};
  color: ${DASH_COLORS.text};
  font-family: ${DASH_FONTS.ui};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: clamp(12px, 2vmin, 48px);
  border-bottom: 1px solid ${DASH_COLORS.tileBorder};
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${DASH_FONTS.numeric};
  font-size: ${DASH_TYPE.label};
  font-weight: 700;
`;

const CloseButton = styled.button`
  appearance: none;
  min-width: ${MIN_TOUCH_PX}px;
  min-height: ${MIN_TOUCH_PX}px;
  width: clamp(${MIN_TOUCH_PX}px, 6vmin, 130px);
  height: clamp(${MIN_TOUCH_PX}px, 6vmin, 130px);
  border-radius: 50%;
  border: 1px solid ${DASH_COLORS.tileBorder};
  background: ${DASH_COLORS.tile};
  color: ${DASH_COLORS.text};
  font-size: clamp(22px, 2.6vmin, 56px);
  line-height: 1;
  cursor: pointer;

  &:active {
    background: ${DASH_COLORS.tilePressed};
  }
  &:focus-visible {
    outline: 3px solid ${DASH_COLORS.accent};
    outline-offset: 2px;
  }
`;

const Content = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: clamp(12px, 2vmin, 48px);
`;

export default function DetailOverlay({ title, closeLabel, onClose, children }) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <Backdrop role="dialog" aria-modal="true" aria-label={title}>
      <Header>
        <Title>{title}</Title>
        <CloseButton type="button" onClick={onClose} aria-label={closeLabel}>✕</CloseButton>
      </Header>
      <Content>{children}</Content>
    </Backdrop>
  );
}
